import { Workstation } from 'app/containers/OrderDetails/types';
import { ProductionPiece } from 'app/models/ProductionPiece';
import {
  getDrawerHardwareGroup,
  getDrawerStandardGroup,
  isDrawerHardwareReference,
} from './drawerPieceClassification';

const BATTEN_GROUP_WEIGHT = 25 * 100;
const FURNITURE_CUP_REFERENCES = new Set([
  'CB',
  'CBL',
  'CBR',
  'CC',
  'CG',
  'CGA',
  'CGS',
  'CJ',
  'CN',
  'CR',
  'CV',
]);
const FURNITURE_CUP_GROUP_WEIGHT =
  BATTEN_GROUP_WEIGHT + 1;
const C4_GROUP_WEIGHT = FURNITURE_CUP_GROUP_WEIGHT + 1;

interface ComparePickingPresentationPiecesOptions {
  forceDrawerHardwareSorting?: boolean;
}

const normalizeReference = (reference: string): string =>
  reference.replace(/ *\[[^\]]*]/, '').trim();

const compareLegacyKeys = (a: ProductionPiece, b: ProductionPiece) => {
  if (a.sortingInType !== b.sortingInType) {
    return a.sortingInType - b.sortingInType;
  }
  if (a.sortingThird !== b.sortingThird) {
    return a.sortingThird - b.sortingThird;
  }
  return a.reference.localeCompare(b.reference);
};

const compareDescending = (a: number, b: number) => b - a;

const getSpecialDReferenceOrder = (piece: ProductionPiece) => {
  const normalizedReference = normalizeReference(piece.reference);
  const d3Match = normalizedReference.match(/^D3-(\d+)$/);

  if (d3Match) {
    return {
      group: 0,
      size: Number(d3Match[1]),
    };
  }

  const dMatch = normalizedReference.match(/^D(\d+)$/);

  if (dMatch) {
    return {
      group: 1,
      size: Number(dMatch[1]),
    };
  }

  return undefined;
};

const startsWithFrenchScrewWord = (piece: ProductionPiece): boolean =>
  /^vis\s/i.test(piece.descriptionFr.trim());

const getDrawerHardwareGroupForPiece = (
  piece: ProductionPiece,
  forceDrawerHardwareSorting = false,
) => {
  if (
    piece.cutting ||
    !isDrawerHardwareReference(piece.reference)
  ) {
    return undefined;
  }

  if (!forceDrawerHardwareSorting && piece.station !== Workstation.Drawer) {
    return undefined;
  }

  return getDrawerHardwareGroup(piece.reference);
};

const compareDrawerHardwareDescriptionPriority = (
  a: ProductionPiece,
  b: ProductionPiece,
  drawerHardwareGroupA: number | undefined,
  drawerHardwareGroupB: number | undefined,
) => {
  if (drawerHardwareGroupA === undefined || drawerHardwareGroupB === undefined) {
    return 0;
  }

  const screwDescriptionA = startsWithFrenchScrewWord(a);
  const screwDescriptionB = startsWithFrenchScrewWord(b);

  if (screwDescriptionA === screwDescriptionB) {
    return 0;
  }

  return screwDescriptionA ? -1 : 1;
};

const getDrawerStandardGroupForPiece = (piece: ProductionPiece) => {
  if (piece.station !== Workstation.Drawer || piece.cutting) {
    return undefined;
  }

  return getDrawerStandardGroup(piece.reference);
};

const isFurnitureCupPiece = (piece: ProductionPiece): boolean => {
  return (
    piece.station !== Workstation.Drawer &&
    !piece.cutting &&
    FURNITURE_CUP_REFERENCES.has(normalizeReference(piece.reference))
  );
};

const getPieceOrderWeight = (piece: ProductionPiece) => {
  if (isFurnitureCupPiece(piece)) {
    return FURNITURE_CUP_GROUP_WEIGHT;
  }

  if (normalizeReference(piece.reference) === 'C4') {
    return C4_GROUP_WEIGHT;
  }

  return piece.sorting * 100;
};

const compareByTypeSize = (a: ProductionPiece, b: ProductionPiece) => {
  const drawerHardwareGroupA = getDrawerHardwareGroupForPiece(a);
  const drawerHardwareGroupB = getDrawerHardwareGroupForPiece(b);
  const drawerHardwareDescriptionPriority =
    compareDrawerHardwareDescriptionPriority(
      a,
      b,
      drawerHardwareGroupA,
      drawerHardwareGroupB,
    );

  if (drawerHardwareDescriptionPriority !== 0) {
    return drawerHardwareDescriptionPriority;
  }

  if (
    drawerHardwareGroupA !== undefined &&
    drawerHardwareGroupB !== undefined &&
    drawerHardwareGroupA !== drawerHardwareGroupB
  ) {
    return drawerHardwareGroupA - drawerHardwareGroupB;
  }

  const drawerStandardGroupA = getDrawerStandardGroupForPiece(a);
  const drawerStandardGroupB = getDrawerStandardGroupForPiece(b);

  if (
    drawerStandardGroupA !== undefined &&
    drawerStandardGroupB !== undefined &&
    drawerStandardGroupA !== drawerStandardGroupB
  ) {
    return drawerStandardGroupA - drawerStandardGroupB;
  }

  const typeName = a.typeName;

  if (typeName !== b.typeName) {
    return compareLegacyKeys(a, b);
  }

  switch (typeName) {
    case 'door':
    case 'pane':
    case 'plate': {
      const compareHeight = compareDescending(a.height, b.height);
      if (compareHeight !== 0) {
        return compareHeight;
      }

      const compareWidth = compareDescending(a.width, b.width);
      if (compareWidth !== 0) {
        return compareWidth;
      }

      return compareLegacyKeys(a, b);
    }
    case 'rod': {
      const compareWidth = compareDescending(a.width, b.width);
      if (compareWidth !== 0) {
        return compareWidth;
      }

      return compareLegacyKeys(a, b);
    }
    case 'feet cabinet':
    case 'batten': {
      const compareHeight = compareDescending(a.height, b.height);
      if (compareHeight !== 0) {
        return compareHeight;
      }

      return compareLegacyKeys(a, b);
    }
    case 'cross piece':
      return compareLegacyKeys(a, b);
    default:
      return compareLegacyKeys(a, b);
  }
};

export const comparePickingPresentationPieces = (
  a: ProductionPiece,
  b: ProductionPiece,
  options: ComparePickingPresentationPiecesOptions = {},
) => {
  const { forceDrawerHardwareSorting = false } = options;

  const drawerHardwareGroupA = getDrawerHardwareGroupForPiece(
    a,
    forceDrawerHardwareSorting,
  );
  const drawerHardwareGroupB = getDrawerHardwareGroupForPiece(
    b,
    forceDrawerHardwareSorting,
  );
  const drawerHardwareDescriptionPriority =
    compareDrawerHardwareDescriptionPriority(
      a,
      b,
      drawerHardwareGroupA,
      drawerHardwareGroupB,
    );

  if (drawerHardwareDescriptionPriority !== 0) {
    return drawerHardwareDescriptionPriority;
  }

  if (
    drawerHardwareGroupA !== undefined &&
    drawerHardwareGroupB !== undefined &&
    drawerHardwareGroupA !== drawerHardwareGroupB
  ) {
    return drawerHardwareGroupA - drawerHardwareGroupB;
  }

  const drawerStandardGroupA = getDrawerStandardGroupForPiece(a);
  const drawerStandardGroupB = getDrawerStandardGroupForPiece(b);

  if (
    drawerStandardGroupA !== undefined &&
    drawerStandardGroupB !== undefined &&
    drawerStandardGroupA !== drawerStandardGroupB
  ) {
    return drawerStandardGroupA - drawerStandardGroupB;
  }

  const specialDReferenceOrderA = getSpecialDReferenceOrder(a);
  const specialDReferenceOrderB = getSpecialDReferenceOrder(b);

  if (
    specialDReferenceOrderA !== undefined &&
    specialDReferenceOrderB !== undefined
  ) {
    if (specialDReferenceOrderA.group !== specialDReferenceOrderB.group) {
      return specialDReferenceOrderA.group - specialDReferenceOrderB.group;
    }

    if (specialDReferenceOrderA.size !== specialDReferenceOrderB.size) {
      return compareDescending(
        specialDReferenceOrderA.size,
        specialDReferenceOrderB.size,
      );
    }
  }

  const pieceOrderWeightA = getPieceOrderWeight(a);
  const pieceOrderWeightB = getPieceOrderWeight(b);

  if (pieceOrderWeightA !== pieceOrderWeightB) {
    return pieceOrderWeightA - pieceOrderWeightB;
  }

  return compareByTypeSize(a, b);
};

export const createComparePickingPresentationPieces = (
  options: ComparePickingPresentationPiecesOptions = {},
) => {
  return (a: ProductionPiece, b: ProductionPiece) =>
    comparePickingPresentationPieces(a, b, options);
};

export default comparePickingPresentationPieces;
